import { useCallback, useEffect, useMemo, useState } from 'react';
import { ContentPanel, usePanel } from 'src/Common/Components/ContentControl/ContentPanel';
import { type SpatialEntityFields, type CompartmentView, type Preset } from '@ekkogmbh/apisdk';
import { Box, Fade, Grid, Stack } from '@mui/material';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import { CoordinateInput } from 'src/Common/Components/CoordinateInput';
import { StyledFormHeader } from 'src/Common/Components/Forms/StyledFormHeader';
import { TemplatePreviewList } from 'src/TemplateManagement/Components/TemplatePreviewList';
import { onKeyPressCallback } from 'src/Common/Helper/FormHelper';
import { PresetPicker } from 'src/PresetManagement/PresetPicker';
import useRequest from 'src/Common/Hooks/useRequest';
import useEkko from 'src/Common/Providers/EkkoProvider';
import useTaskCollections from 'src/Common/Providers/TaskCollectionProvider';
import usePagination from 'src/Common/Providers/PaginationProvider';
import { ReactiveRow } from 'src/Common/Components/Stacks';

interface Props {
  compartment?: CompartmentView;
  closeHandler: () => void;
}

export const CompartmentPanel = ({ compartment, closeHandler }: Props): React.JSX.Element => {
  const { api, config } = useEkko();
  const { subscribe } = useTaskCollections();
  const { enableSave, isSaveEnabled } = usePanel();
  const { refresh } = usePagination();

  const [isSaving, startSaving] = useRequest();
  const [selectedPreset, setSelectedPreset] = useState<Preset>();
  const isEditMode = useMemo(() => compartment !== undefined, [compartment]);

  /** input stores */
  const [coordinate, setCoordinate] = useState<string>('');
  const [fields, setFields] = useState<SpatialEntityFields>({});

  useEffect(() => {
    enableSave(!isSaving && coordinate !== '');
  }, [coordinate, isSaving]);
  /** --- */

  /** init state with compartment and/or preset given */
  const reset = useCallback(() => {
    setCoordinate(compartment?.coordinate ?? '');
    setFields(compartment?.fields ?? {});
    if (isEditMode) {
      // we don't know which preset created a compartment
      setSelectedPreset(undefined);
    }
  }, [compartment, isEditMode]);
  useEffect(() => {
    reset();
  }, [reset]);

  /** Fields can only have preset fields */
  useEffect(() => {
    if (!!selectedPreset?.keys) {
      const coercedFields: SpatialEntityFields = {};
      selectedPreset.keys.forEach((k) => {
        coercedFields[k] = fields[k] ?? '';
      });
      setFields(coercedFields);
    }
  }, [selectedPreset]);

  const saveCompartment = useCallback(() => {
    startSaving(
      () =>
        api.saveCompartment(
          {
            coordinate,
            fields,
          },
          subscribe,
        ),
      () => {
        refresh();
        closeHandler();
      },
    );
  }, [coordinate, fields, api, subscribe]);

  const coordinateInput = useMemo(() => {
    const isPresetSelected = selectedPreset !== undefined;
    const coordinatePrefix = selectedPreset?.coordinatePrefix ?? '';
    const trimmedCoordinate =
      isPresetSelected && coordinate.startsWith(coordinatePrefix)
        ? coordinate.replace(coordinatePrefix, '')
        : coordinate;

    return (
      <Fade in timeout={200}>
        <Grid container spacing={1}>
          {!isEditMode && isPresetSelected && (
            <Grid item xs={6}>
              <StyledTextField
                onChange={() => console.log('is disabled')}
                label={'Coordinate Prefix'}
                disabled={true}
                value={coordinatePrefix}
              />
            </Grid>
          )}
          <Grid item xs={isEditMode ? 12 : 6}>
            {(isPresetSelected || isEditMode) && (
              <CoordinateInput
                disabled={isEditMode}
                onChange={(coord) => {
                  setCoordinate(coordinatePrefix + coord);
                }}
                value={isEditMode ? coordinate : trimmedCoordinate}
                trimPrefix={coordinatePrefix}
                trailingDelimiter="both"
              />
            )}
          </Grid>
        </Grid>
      </Fade>
    );
  }, [coordinate, isEditMode, selectedPreset, setCoordinate]);

  const fieldsInput = useMemo(() => {
    const keys = Object.keys(fields);

    if (keys.length === 0) {
      return <></>;
    }

    const fieldElements = keys.map((key: string, i: number) => (
      <Fade in timeout={200} key={i}>
        <Grid item lg={6} xs={12}>
          <StyledTextField
            name={key}
            label={key}
            value={fields[key]}
            onChange={(e) => setFields({ ...fields, [key]: e.target.value })}
          />
        </Grid>
      </Fade>
    ));

    return (
      <Grid container spacing={1}>
        <Grid item xs={12}>
          <StyledFormHeader label={'Fields'} />
        </Grid>
        <Grid container item xs={12} spacing={1}>
          {fieldElements}
        </Grid>
      </Grid>
    );
  }, [fields, setFields]);

  const templatePreview = useMemo(() => {
    if ((compartment?.links ?? []).length === 0) {
      return undefined;
    }

    const templates = compartment!.links.map((l) => l.template);

    const uniqueTemplates = templates.filter(
      (value, index, self) =>
        index === self.findIndex((template) => template.id === value.id && template.name === value.name),
    );

    if (uniqueTemplates.length === 0) {
      return <></>;
    }

    const { coordinateFieldName, labelIdFieldName } = config;

    return (
      <Fade in timeout={200}>
        <Stack direction={'column'} spacing={1}>
          <StyledFormHeader label={'Linked Templates'} />
          <TemplatePreviewList
            templates={uniqueTemplates}
            fields={fields}
            coordinate={coordinate}
            coordinateFieldName={coordinateFieldName}
            labelIdFieldName={labelIdFieldName}
          />
        </Stack>
      </Fade>
    );
  }, [compartment?.links, config, coordinate, fields]);

  const form = useMemo(() => {
    const handleKeyPress = onKeyPressCallback(saveCompartment, isSaveEnabled, 'Enter');
    const sxWidth = { xs: '100%', lg: '60%' };
    return (
      <Stack direction={'column'} spacing={2} alignItems={'center'} justifyContent={'flex-start'}>
        <StyledFormHeader label={'Compartment'} />
        <Stack direction={'column'} spacing={2} sx={{ width: sxWidth }}>
          <PresetPicker optional={isEditMode} value={selectedPreset} onChange={setSelectedPreset} />
          <div>{coordinateInput}</div>
        </Stack>
        {isEditMode ? (
          <ReactiveRow stretch={false} onKeyDown={handleKeyPress} sx={{ width: { xs: '100%', lg: '80%' } }}>
            <Box flex={1}>{fieldsInput}</Box>
            {compartment!.links.length > 0 && <Box sx={{ width: { xs: '100%', md: '30%' } }}>{templatePreview}</Box>}
          </ReactiveRow>
        ) : (
          <Box sx={{ width: sxWidth }}>{fieldsInput}</Box>
        )}
      </Stack>
    );
  }, [
    isSaveEnabled,
    selectedPreset,
    isEditMode,
    coordinateInput,
    fieldsInput,
    templatePreview,
    compartment,
    saveCompartment,
  ]);

  return (
    <ContentPanel
      isLoading={isSaving}
      steps={[
        {
          title: 'Compartment',
          contentCallback: () => form,
        },
      ]}
      onCancel={() => {
        closeHandler();
      }}
      onSave={saveCompartment}
      onReset={reset}
    />
  );
};
